/* eslint-disable no-mixed-spaces-and-tabs */
import { useEffect, useState } from "react";
import { t } from "i18next";
import Feature from "proximiio-js-library/lib/models/feature";
import {
  FaFlag,
  FaPersonWalking,
  FaArrowRotateRight,
  FaRoad,
  FaMapPin,
} from "react-icons/fa6";
import { PiArrowFatLeftFill, PiArrowFatRightFill } from "react-icons/pi";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

import useMapStore from "@/store/mapStore";

export interface StepModel {
  bearingFromLastStep: number;
  coordinates: { coordinates: [number, number] };
  direction: string;
  distanceFromLastStep: number;
  instruction?: string[] | string;
  isWaypoint: boolean;
  level: number;
  levelChangerId: string;
  lineStringFeatureFromLastStep: Feature;
  waypointId: string;
  destinationLevel?: number;
  stepsUntil?: StepModel[];
  totalDistance?: number;
  description?: string;
  maneuver?: {
    type: string;
  };
  distance?: number;
  navMode?: string;
}

function PoiDetailsSteps() {
  const landmarkTBT =
    import.meta.env.VITE_WAYFINDING_ENABLE_LANDMARK_TBT === "true";
  const [steps, setSteps] = useState([] as StepModel[]);

  const map = useMapStore((state) => state.map);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const routeDetails = useMapStore((state) => state.routeDetails);
  const floors = useMapStore((state) => state.floors);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const currentLanguage = useMapStore((state) => state.currentLang);
  const currentStep = useMapStore((state) => state.currentStep);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const navMode = useMapStore((state) => state.navMode);
  const haveCustomPosition = useMapStore((state) => state.haveCustomPosition);
  const reachedDestination = useMapStore((state) => state.reachedDestination);
  const setCurrentStep = useMapStore((state) => state.setCurrentStep);
  const setStepAnimated = useMapStore((state) => state.setStepAnimated);
  const resetAnimatedSteps = useMapStore((state) => state.resetAnimatedSteps);

  const capitalize = (s: string) =>
    s && String(s[0]).toUpperCase() + String(s).slice(1);

  useEffect(() => {
    if (haveRouteDetails) {
      const textNavSteps: StepModel[] = routeDetails.TBTNav.steps;
      if (navMode === "mall") {
        setSteps(
          textNavSteps.map((step) => {
            return {
              ...step,
              description: capitalize(step.instruction as string),
            };
          }),
        );
      } else {
        setSteps(
          textNavSteps.map((step) => {
            if (step.maneuver?.type === "arrive") {
              step.direction = "FINISH";
            }
            step.description = step.instruction?.toString();
            return {
              ...step,
            };
          }),
        );
      }
    }
  }, [
    haveRouteDetails,
    routeDetails?.TBTNav?.steps,
    currentFloor,
    currentLanguage,
    floors,
    navMode,
    landmarkTBT,
  ]);

  const startOverHandler = () => {
    setCurrentStep(0);
    resetAnimatedSteps();
    setTimeout(() => {
      setStepAnimated(0);
    }, 2000);

    map.setNavStep(0);
    if (steps.length === 1 || currentStep === 0) {
      map.restartRouteAnimation({ delay: 0 });
    }

    //}
  };

  const getDistance = (distance: number) => {
    return distance > 1000
      ? `${(distance / 1000).toFixed(1)} ${
          currentLanguage === "en" ? "km" : "كلم"
        }`
      : `${distance.toFixed(0)} ${currentLanguage === "en" ? "m" : "ﻡ"}`;
  };

  const setStepHandler = (step: "previous" | "next") => {
    if (step === "previous") {
      setCurrentStep(currentStep - 1);
      map.setNavStep("previous");
      setTimeout(() => {
        map.stopRouteAnimation(true);
      }, 20);
    } else {
      setCurrentStep(currentStep + 1);
      map.setNavStep("next");
      map.restartRouteAnimation({ delay: 0 });
    }
  };

  if (mobileMode) {
    return (
      <>
        <div className="py-2">
          {steps[currentStep]?.navMode === "city" ? (
            <div className="mt-4 flex items-center gap-4 text-center">
              {!haveCustomPosition && (
                <Button
                  size="icon"
                  variant={"outline"}
                  className="h-8 w-8 shrink-0 rounded-full border-primary text-primary hover:border-primary/60 hover:text-primary/60"
                  onClick={() => setStepHandler("previous")}
                  disabled={currentStep === 0}
                >
                  <PiArrowFatLeftFill className="text-base" />
                </Button>
              )}
              <div
                className={cn(
                  "flex min-h-24 flex-1 items-center gap-2 rounded-lg bg-gray-100 p-2",
                  steps.length - 1 === currentStep &&
                    !haveCustomPosition &&
                    "opacity-1",
                )}
              >
                <span className="text-base text-primary">
                  {steps[currentStep]?.direction === "FINISH" &&
                  !haveCustomPosition ? (
                    <FaMapPin />
                  ) : (
                    <FaRoad />
                  )}
                </span>
                <span className="flex-1 text-sm">
                  {steps[currentStep]?.description}
                </span>
                {steps[currentStep]?.direction !== "FINISH" && (
                  <span className="border-foreground/10 text-xs font-bold group-data-[dir=ltr]:border-l group-data-[dir=rtl]:border-r group-data-[dir=ltr]:pl-3 group-data-[dir=rtl]:pr-3">
                    {getDistance(steps[currentStep]?.distance || 0)}
                  </span>
                )}
              </div>
              {!haveCustomPosition && (
                <Button
                  size="icon"
                  variant={"outline"}
                  className="h-8 w-8 shrink-0 rounded-full border-primary text-primary hover:border-primary/60 hover:text-primary/60"
                  onClick={() => setStepHandler("next")}
                  disabled={currentStep === steps.length - 1}
                >
                  <PiArrowFatRightFill className="text-base" />
                </Button>
              )}
            </div>
          ) : landmarkTBT ? (
            <>
              <div className="mb-3 mt-4 flex items-center gap-4 text-center">
                {!haveCustomPosition && (
                  <Button
                    size="icon"
                    variant={"outline"}
                    className="h-8 w-8 shrink-0 rounded-full border-primary text-primary"
                    onClick={() => setStepHandler("previous")}
                    disabled={currentStep === 0}
                  >
                    <PiArrowFatLeftFill className="text-base" />
                  </Button>
                )}
                <div
                  className={cn(
                    "flex min-h-24 flex-1 items-center gap-2 rounded-lg bg-gray-100 p-2",
                    ((steps.length - 1 === currentStep &&
                      !haveCustomPosition) ||
                      reachedDestination) &&
                      "opacity-1 bg-black/85 text-white",
                  )}
                >
                  <span className="text-xl text-primary">
                    {(steps[currentStep]?.direction === "FINISH" &&
                      !haveCustomPosition) ||
                    reachedDestination ? (
                      <FaFlag className="text-white" />
                    ) : (
                      <FaPersonWalking />
                    )}
                  </span>
                  <span className="text-sm">
                    {reachedDestination
                      ? t("you-have-arrived")
                      : steps[currentStep]?.description}
                  </span>
                </div>
                {!haveCustomPosition && (
                  <Button
                    size="icon"
                    variant={"outline"}
                    className="h-8 w-8 shrink-0 rounded-full border-primary text-primary"
                    onClick={() => setStepHandler("next")}
                    disabled={currentStep === steps.length - 1}
                  >
                    <PiArrowFatRightFill className="text-base" />
                  </Button>
                )}
              </div>
              {!haveCustomPosition && (
                <Button
                  className="float-left h-7 w-7 rounded-full p-0"
                  onClick={startOverHandler}
                >
                  <FaArrowRotateRight className="text-sm" />
                </Button>
              )}
            </>
          ) : (
            <>
              <div className="my-0 flex items-center justify-center gap-2">
                {/*<p
                  className={cn(
                    "flex flex-1 items-center gap-4 rounded-lg bg-gray-50 p-4 text-sm",
                    ((steps.length - 1 === currentStep &&
                      !haveCustomPosition) ||
                      reachedDestination) &&
                      "opacity-1 bg-black/85 text-white",
                  )}
                >
                  <span className="text-xl text-primary">
                    {(steps[currentStep]?.direction === "FINISH" &&
                      !haveCustomPosition) ||
                    reachedDestination ? (
                      <FaFlag className="text-white" />
                    ) : (
                      <FaPersonWalking />
                    )}
                  </span>
                  <span>
                    {reachedDestination
                      ? t("you-have-arrived")
                      : steps[currentStep]?.description}
                  </span>
                </p>*/}

                <Button
                  className="rounded-full p-0 px-4"
                  onClick={startOverHandler}
                >
                  <FaArrowRotateRight className="mr-2 text-sm" />
                  {t("start-from-beginning")}
                </Button>
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="py-5">
        {steps[currentStep]?.navMode === "city" ? (
          <div
            className={cn(
              "flex min-h-32 items-center gap-4 rounded-lg bg-gray-100 p-4",
              steps.length - 1 === currentStep &&
                !haveCustomPosition &&
                "opacity-1",
            )}
          >
            <span className="text-xl text-primary">
              {steps[currentStep]?.direction === "FINISH" &&
              !haveCustomPosition ? (
                <FaMapPin />
              ) : (
                <FaRoad />
              )}
            </span>
            <span className="flex-1">{steps[currentStep]?.description}</span>
            {steps[currentStep]?.direction !== "FINISH" && (
              <span className="border-foreground/10 font-bold group-data-[dir=ltr]:border-l group-data-[dir=rtl]:border-r group-data-[dir=ltr]:pl-3 group-data-[dir=rtl]:pr-3">
                {getDistance(steps[currentStep]?.distance || 0)}
              </span>
            )}
          </div>
        ) : (
          <div className="flex">
            <ul className={cn("flex w-full flex-col gap-2")}>
              {steps
                .slice(currentStep, reachedDestination ? 1 : currentStep + 2)
                .map((step, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex items-center gap-4 rounded-lg bg-gray-100 px-4 py-2",
                      index === 0 ? "" : "opacity-25",
                      ((steps.length - 1 === currentStep &&
                        !haveCustomPosition) ||
                        reachedDestination) &&
                        "opacity-1 bg-black/85 text-white",
                    )}
                  >
                    <span className="text-xl text-primary">
                      {(step.direction === "FINISH" && !haveCustomPosition) ||
                      reachedDestination ? (
                        <FaFlag className="text-white" />
                      ) : (
                        <FaPersonWalking />
                      )}
                    </span>
                    <span>
                      {reachedDestination
                        ? t("you-have-arrived")
                        : step.description}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        )}
        {steps[currentStep]?.navMode === "city"
          ? !haveCustomPosition && (
              <div className="mt-4 flex gap-4 text-center">
                <Button
                  size="lg"
                  variant={"outline"}
                  className="flex w-full gap-0.5 rounded-full border-primary text-primary hover:border-primary/60 hover:text-primary/60"
                  onClick={() => setStepHandler("previous")}
                  disabled={currentStep === 0}
                >
                  <PiArrowFatLeftFill className="text-lg group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
                  {t("previous")}
                </Button>
                <Button
                  size="lg"
                  variant={"outline"}
                  className="flex w-full gap-0.5 rounded-full border-primary text-primary hover:border-primary/60 hover:text-primary/60"
                  onClick={() => setStepHandler("next")}
                  disabled={currentStep === steps.length - 1}
                >
                  {t("next")}
                  <PiArrowFatRightFill className="text-lg group-data-[dir=ltr]:ml-2 group-data-[dir=rtl]:mr-2" />
                </Button>
              </div>
            )
          : !haveCustomPosition && (
              <div className="mt-4 text-center">
                <Button
                  size="lg"
                  className="flex w-full gap-0.5"
                  onClick={startOverHandler}
                >
                  <FaArrowRotateRight className="text-lg group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
                  {t("replay")}
                </Button>
              </div>
            )}
      </div>
    </>
  );
}

export default PoiDetailsSteps;
